"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { composeRefs, useComposedRefs } from "@radix-ui/react-compose-refs";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import * as Form from "@radix-ui/react-form";
import { Grid } from "@radix-ui/themes";
import * as React from "react";
import { TextField } from "./elements";
const OtpContext = React.createContext(undefined);
export const Root = React.forwardRef(function Root({ name, id, defaultValue, value: valueProp, onValueChange, autoSubmit, children, readOnly, state, ...gridProps }, forwardedRef) {
    const [lastCharIndex, setLastCharIndex] = React.useState(0);
    const [allChildrenAdded, setAllChildrenAdded] = React.useState(false);
    const childCount = React.Children.count(children);
    const [value, setValue] = useControllableState({
        prop: getValueAsArray(valueProp, childCount),
        defaultProp: getValueAsArray(defaultValue, childCount),
        onChange: (value) => onValueChange?.(value.join("")),
    });
    const hiddenInputRef = React.useRef(null);
    const childrenRefs = React.useRef([]);
    const attemptAutoSubmit = React.useCallback((enterPressed = false) => {
        if (autoSubmit &&
            value &&
            value.every((char) => char !== "") &&
            (enterPressed || lastCharIndex + 1 === childCount)) {
            hiddenInputRef.current?.form?.requestSubmit();
        }
    }, [value, childCount, lastCharIndex, autoSubmit]);
    const handleEnterPressed = React.useCallback(() => attemptAutoSubmit(true), [attemptAutoSubmit]);
    const handleChildAdd = React.useCallback((input) => {
        if (input) {
            input.dataset.index = `${childrenRefs.current.length}`;
            childrenRefs.current.push(input);
        }
        else {
            childrenRefs.current.pop();
        }
        if (childrenRefs.current.length === childCount) {
            setAllChildrenAdded(true);
        }
    }, [childCount]);
    const handleCharChange = React.useCallback((char, index) => {
        setValue((previousValue) => {
            const arrayToCopy = previousValue ?? createEmptyArray(childCount);
            const newValue = [...arrayToCopy];
            newValue[index] = char;
            return newValue;
        });
        setLastCharIndex(index);
    }, [childCount, setValue]);
    const otpContext = React.useMemo(() => ({
        value: value ?? createEmptyArray(childCount),
        readOnly,
        state,
        allChildrenAdded,
        onEnterPressed: handleEnterPressed,
        onChildAdd: handleChildAdd,
        onCharChange: handleCharChange,
    }), [
        value,
        allChildrenAdded,
        readOnly,
        state,
        childCount,
        handleEnterPressed,
        handleChildAdd,
        handleCharChange,
    ]);
    React.useEffect(attemptAutoSubmit, [attemptAutoSubmit]);
    return (_jsx(OtpContext.Provider, { value: otpContext, children: _jsxs(Grid, { columns: `repeat(${childCount}, 1fr)`, ...gridProps, onPaste: (event) => {
                event.preventDefault();
                const pastedValue = event.clipboardData.getData("Text");
                const sanitizedValue = pastedValue
                    .replace(/[^\d]/g, "")
                    .slice(0, childCount);
                const value = sanitizedValue
                    .padEnd(childCount, "#")
                    .split("")
                    .map((char) => (char === "#" ? "" : char));
                setValue(value);
                setLastCharIndex(sanitizedValue.length - 1);
                const index = Math.min(sanitizedValue.length, childCount - 1);
                childrenRefs.current?.[index]?.focus();
            }, children: [children, _jsx("input", { ref: composeRefs(forwardedRef, hiddenInputRef), defaultValue: value?.join(""), minLength: childCount, name: name, type: "hidden" })] }) }));
});
export const Input = React.forwardRef(function Input({ style, readOnly, autoComplete = "off", ...props }, forwardedRef) {
    const otpContext = useOptContext();
    const inputRef = React.useRef(null);
    const composedInputRef = useComposedRefs(forwardedRef, inputRef, otpContext.onChildAdd);
    const index = Number(inputRef.current?.dataset.index ?? -1);
    const char = otpContext.value[index] ?? "";
    return (_jsx(Form.Field, { name: `otp-${index}`, asChild: true, children: _jsx(Form.Control, { asChild: true, children: _jsx(TextField, { ref: composedInputRef, autoComplete: index === 0 ? autoComplete : "off", color: otpContext.state === "invalid" ? "red" : undefined, inputMode: "numeric", maxLength: 1, pattern: "\\d{1}", readOnly: readOnly ?? otpContext.readOnly, size: "3", value: char, variant: otpContext.state === "invalid" ? "soft" : undefined, style: {
                    ...style,
                    height: "auto",
                    "--text-field-padding": 0,
                    textAlign: "center",
                }, onChange: (event) => {
                    // Only update the value if it matches the input pattern (number only)
                    if (event.target.validity.valid) {
                        const char = event.target.value;
                        const index = Number(event.target.dataset.index ?? -1);
                        otpContext.onCharChange(char, index);
                        if (char !== "") {
                            focusSibling(event.currentTarget, { back: char === "" });
                        }
                    }
                }, onKeyDown: (event) => {
                    if (event.key === "ArrowLeft") {
                        focusSibling(event.currentTarget, { back: true });
                        event.preventDefault();
                    }
                    else if (event.key === "ArrowRight") {
                        focusSibling(event.currentTarget);
                        event.preventDefault();
                    }
                    else if (event.key === "Backspace" && char === "") {
                        focusSibling(event.currentTarget, { back: true });
                    }
                    else if (event.key === "Enter" && char !== "") {
                        otpContext.onEnterPressed();
                    }
                }, ...props }) }) }));
});
const useOptContext = () => {
    const optContext = React.useContext(OtpContext);
    if (!optContext) {
        throw new Error("OtpInput compound components cannot be rendered outside the OtpRoot component");
    }
    return optContext;
};
function focusSibling(input, { back = false } = {}) {
    const sibling = back
        ? input.parentElement?.previousSibling
        : input.parentElement?.nextSibling;
    const siblingInput = sibling?.firstChild;
    if (siblingInput && siblingInput instanceof HTMLInputElement) {
        siblingInput?.focus();
        siblingInput?.select();
    }
}
const getValueAsArray = (value, length) => {
    if (!value) {
        return undefined;
    }
    return createEmptyArray(length).map((_, index) => value?.[index] ?? "");
};
const createEmptyArray = (length) => Array.from({ length }).fill("");
//# sourceMappingURL=otp-input.js.map